import {Exclude, Expose, instanceToPlain, plainToClass, Transform} from "class-transformer";
import * as t from "io-ts";
import {v4 as uuid} from "uuid";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {SiteType, toSiteType} from "../site-type";
import {getDatabaseCodeTransformer} from "../transfomers/transformers";

export const PipelineGraphNodeValidator = t.intersection([
  t.type({
    id: t.string,
    pipelineGraphId: t.string,
    objectId: t.string,
    objectType: t.string,
  }),
  t.partial({
    objectName: t.string,
    siteType: t.string,
    billable: t.boolean,
    overrideFreightTypeId: t.string,
    comments: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const PipelineGraphNodeArrayValidator = t.array(
  PipelineGraphNodeValidator
);

export type PipelineGraphNode = t.TypeOf<typeof PipelineGraphNodeValidator>;
export type PipelineGraphNodeArray = t.TypeOf<
  typeof PipelineGraphNodeArrayValidator
>;

@Exclude()
export class PipelineGraphNodeEntity implements Entity {
  @Expose() id!: string;
  @Expose() pipelineGraphId!: string;
  @Expose() objectId!: string;
  @Expose() objectType!: string;
  @Expose() objectName?: string;
  @Expose() billable?: boolean;
  @Expose() overrideFreightTypeId?: string;

  @Transform(getDatabaseCodeTransformer(toSiteType))
  @Expose()
  siteType?: SiteType;

  @Expose() comments?: string;
  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;

  readonly entityType = 'PIPELINEGRAPHNODE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for PipelineGraphNodeEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static builder(id: string = uuid()): GraphBuilderStep {
    return new PipelineGraphNodeBuilder(id);
  }

  static create(builder: PipelineGraphNodeBuilder): PipelineGraphNodeEntity {
    const entity = new PipelineGraphNodeEntity();
    entity.id = builder.id;
    entity.pipelineGraphId = builder.pipelineGraphId;
    entity.objectId = builder.objectId;
    entity.objectType = builder.objectType;
    entity.siteType = builder.siteType;
    entity.billable = builder.billable;
    entity.overrideFreightTypeId = builder.overrideFreightTypeId;
    entity.comments = builder.comments;
    entity.createDttm = builder.createDttm;
    entity.lastModifiedDttm = builder.lastModifiedDttm;
    return entity;
  }

  static from(node: PipelineGraphNode): PipelineGraphNodeEntity {
    const entity: PipelineGraphNodeEntity = plainToClass(
      PipelineGraphNodeEntity,
      node,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  loadPlainValue(): Promise<PipelineGraphNode> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(PipelineGraphNodeValidator, object);
  }

  asPlainValue(): PipelineGraphNode {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSONNow(PipelineGraphNodeValidator, object);
  }
}

interface GraphBuilderStep {
  setPipelineGraph: (value: string) => ObjectBuilderStep;
}

interface ObjectBuilderStep {
  setObject: (objectId: string, objectType: string) => PipelineGraphNodeBuilder;
}

class PipelineGraphNodeBuilder implements GraphBuilderStep, ObjectBuilderStep {
  id: string;
  pipelineGraphId!: string;
  objectId!: string;
  objectType!: string;
  siteType?: SiteType = undefined;
  billable = false;
  overrideFreightTypeId?: string = undefined;
  comments?: string = undefined;
  createDttm?: number = undefined;
  lastModifiedDttm?: number = undefined;

  constructor(id: string = uuid()) {
    this.id = id;
  }

  setPipelineGraph(value: string): ObjectBuilderStep {
    this.pipelineGraphId = value;
    return this;
  }

  setObject(objectId: string, objectType: string): PipelineGraphNodeBuilder {
    this.objectId = objectId;
    this.objectType = objectType;
    return this;
  }

  setSiteType(value: SiteType | undefined): PipelineGraphNodeBuilder {
    this.siteType = value;
    return this;
  }

  setBillable(value: boolean): PipelineGraphNodeBuilder {
    this.billable = value;
    return this;
  }

  setOverrideFreightTypeId(
    value: string | undefined
  ): PipelineGraphNodeBuilder {
    this.overrideFreightTypeId = value;
    return this;
  }

  setComments(value: string | undefined): PipelineGraphNodeBuilder {
    this.comments = value;
    return this;
  }

  setCreateDttm(value: number | undefined): PipelineGraphNodeBuilder {
    this.createDttm = value;
    return this;
  }

  setLastModifiedDttm(value: number | undefined): PipelineGraphNodeBuilder {
    this.lastModifiedDttm = value;
    return this;
  }

  /* istanbul ignore next */
  private isValid(): boolean {
    return (
      this.id != undefined &&
      this.pipelineGraphId != undefined &&
      this.objectId != undefined &&
      this.objectType != undefined
    );
  }

  build(): PipelineGraphNodeEntity {
    if (!this.isValid()) {
      throw Error(
        'The following fields must be defined: ID, PipelineGraphID, ObjectID, & ObjectType.'
      );
    }

    return PipelineGraphNodeEntity.create(this);
  }
}
